import { useSelector } from 'react-redux';
import { useSWRConfig } from 'swr';
import useSWRImmutable from 'swr/immutable';
import { _ } from '/@/locale';
import {
    getNitrogenSourcesFromAPI,
    getNSRecommendationFromAPI,
    getNSRecommendationsFromAPI,
} from '/@/store/nitrogen-recommendation/apiProviders';
import { getBrands } from '/@/store/nitrogen-recommendation/selectors';
import { getSessionCountry } from '/@/store/session/selectors';

/**
 * @typedef {Object} Brand
 * @property {string} codename
 * @property {number} cornHeadToHeadMinimumTrials
 * @property {number} countryId
 * @property {number} cropSpacing
 * @property {number} id
 * @property {string} logo
 * @property {string} logoCodename
 * @property {number} metricsMaxValue
 * @property {string} name
 * @property {number} order
 * @property {boolean} showDensityChart
 * @property {boolean} showEstimatedYieldInProductSelection
 * @property {boolean} showMetricsReference
 * @property {boolean} showMetricsUsingBrandTheme
 * @property {boolean} showMetricsValues
 * @property {boolean} showPrescriptionsLink
 * @property {boolean} showShortDescriptionInProductSelection
 * @property {boolean} showYieldComparison
 * @property {string} slug
 * @property {object} soyHeadToHeadMinimumTrials
 * @property {string} theme
 * @property {string} type
 * @property {boolean} published
 *
 * @returns {Brand}
 * */
export const useBrand = () => {
    return useSelector((state) => getBrands(state)[0]); // TODO: BRAND
};

/**
 * @typedef {Object} NitrogenRecommendation
 * @property {'fixed'|string} type
 * @property {string} technique
 * @property {string} plantingDate
 * @property {string} phone
 * @property {string} distributor
 * @property {string} estimatedApplicationDate
 * @property {string | undefined} [applicationDate]
 * @property {number} hybrid
 * @property {ManagementZones[]} managementZones
 * @property {string} companyName
 * @property {number} nitrogenSource
 * @property {{file: string, recommendation: string}[]} [results]
 * @property {any[]} managementZoneFilesIds
 * @property {string} field
 * @property {string} status
 * @property {number} appInstance
 * @property {string} id
 * @property {string} created
 * @property {string} _id
 * @property {string} _rev
 * @property {ManagementZoneFiles[]} [managementZoneFiles]
 */

/**
 * @typedef {{id: string, file: string}} ManagementZoneFiles
 */

/**
 * @typedef {Object} ICountry
 * @property {object} altitude
 * @property {Bounds} bounds
 * @property {string} code
 * @property {number} id
 * @property {string} language
 * @property {string} name
 * @property {object} parent
 */

/**
 * @typedef {Object} Bounds
 * @property {number} east
 * @property {number} north
 * @property {number} south
 * @property {number} west
 * @returns {ICountry}
 */
export const useCountry = () => useSelector(getSessionCountry);

export const useNSRecommendationById = (recoId) => {
    const { cache } = useSWRConfig();
    let key = {
        key: 'myNSRecommendations',
        recoId,
    };
    if (!recoId) key = null;
    return useSWRImmutable(key, (recoId) =>
        getNSRecommendationFromAPI(
            {
                key: 'myNSRecommendations',
                recoId,
            },
            cache
        )
    );
};

export const useNSRecommendations = () => {
    const country = useSelector(getSessionCountry);
    const key = {
        key: 'allMyNSRecommendations',
        country: country.code,
    };
    return useSWRImmutable(key, getNSRecommendationsFromAPI);
};

export const useNSRecommendationsOptions = () => {
    const recos = useNSRecommendations();

    const output = { ...recos };
    if (recos.data) {
        output.data = (recos.data || []).map((field) => ({
            value: field.id,
            label: `${_('Lote')} ${field.name}, ${field.location}`,
        }));
    }
    return output;
};

export const useNitrogenSources = () => {
    const key = 'nitrogenSources';
    return useSWRImmutable(key, () => getNitrogenSourcesFromAPI());
};

export const useNitrogenSource = (id: number) => {
    const sources = useNitrogenSources();
    return sources.data?.find((source) => source.id === id);
};
